import { useState, useEffect, useRef, useCallback } from "react";

const DERIV_WS_URL = "wss://ws.binaryws.com/websockets/v3?app_id=1089";

const MARKETS = [
  { symbol: "R_10",    label: "Volatility 10",  color: "#00d4aa" },
  { symbol: "R_25",    label: "Volatility 25",  color: "#00b4d8" },
  { symbol: "R_50",    label: "Volatility 50",  color: "#f4a261" },
  { symbol: "R_75",    label: "Volatility 75",  color: "#e76f51" },
  { symbol: "R_100",   label: "Volatility 100", color: "#e63946" },
  { symbol: "1HZ10V",  label: "Vol 10 (1s)",    color: "#48cae4" },
  { symbol: "1HZ25V",  label: "Vol 25 (1s)",    color: "#0096c7" },
  { symbol: "1HZ50V",  label: "Vol 50 (1s)",    color: "#fb8500" },
  { symbol: "1HZ75V",  label: "Vol 75 (1s)",    color: "#d62828" },
  { symbol: "1HZ100V", label: "Vol 100 (1s)",   color: "#9b2226" },
];

const RISK = {
  conservative: { stakeMultiplier: 1.5, maxConsecLoss: 2, takeProfit: 3,  stopLoss: 5,  minConfidence: 0.80 },
  balanced:     { stakeMultiplier: 2.0, maxConsecLoss: 2, takeProfit: 5,  stopLoss: 8,  minConfidence: 0.75 },
  aggressive:   { stakeMultiplier: 2.5, maxConsecLoss: 3, takeProfit: 10, stopLoss: 15, minConfidence: 0.70 },
};

// ── SWITCH THRESHOLD: how much better the new market must be to trigger a swap
const MARKET_SWITCH_THRESHOLD = 12; // score points above current market
// ── DRIFT GUARD: if active market confidence drops below this, force rescan
const DRIFT_CONFIDENCE_FLOOR  = 0.58;
// ── RESCAN INTERVAL: continuous background rescoring (ms)
const RESCAN_INTERVAL_MS      = 3000;

function getLastDigit(price) {
  const s = price.toFixed(5);
  return parseInt(s[s.length - 1], 10);
}

function analyseDigits(prices) {
  if (!prices || prices.length < 30) return null;

  const short  = prices.slice(-15);
  const medium = prices.slice(-30);
  const long   = prices.slice(-Math.min(80, prices.length));

  function bias(arr) {
    const over  = arr.filter(p => getLastDigit(p) >= 5).length / arr.length;
    const under = arr.filter(p => getLastDigit(p) <= 4).length / arr.length;
    return { over, under, dir: over >= under ? "OVER" : "UNDER" };
  }

  function momentum(arr, dir) {
    let same = 0;
    for (let i = arr.length - 1; i >= arr.length - 5 && i > 0; i--) {
      const d = getLastDigit(arr[i]);
      if (dir === "OVER" && d >= 5) same++;
      else if (dir === "UNDER" && d <= 4) same++;
    }
    return same / 5;
  }

  const bs = bias(short);
  const bm = bias(medium);
  const bl = bias(long);
  const agree = bs.dir === bm.dir && bm.dir === bl.dir;
  const dir = bm.dir;
  const confS = dir === "OVER" ? bs.over  : bs.under;
  const confM = dir === "OVER" ? bm.over  : bm.under;
  const confL = dir === "OVER" ? bl.over  : bl.under;
  const confidence = (confS * 0.40 + confM * 0.35 + confL * 0.25) * (agree ? 1 : 0.5);
  const mom = momentum(short, dir);

  const freq = Array(10).fill(0);
  medium.forEach(p => freq[getLastDigit(p)]++);
  const total = medium.length;
  const hotDigits = freq.map((c, i) => ({ d: i, pct: (c / total) * 100 })).sort((a, b) => b.pct - a.pct);
  const contractType = dir === "OVER" ? "DIGITOVER" : "DIGITUNDER";
  const barrier = contractType === "DIGITOVER" ? 4 : 5;

  return { freq, hotDigits, total, contractType, barrier, confidence, agree, overRate: bm.over, underRate: bm.under, momentum: mom };
}

function scoreMarket(prices) {
  const a = analyseDigits(prices);
  if (!a || !a.agree) return 0;
  return a.confidence * 100;
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────
export default function DerivBot() {
  const [apiToken, setApiToken]           = useState("");
  const [connected, setConnected]         = useState(false);
  const [authorized, setAuthorized]       = useState(false);
  const [balance, setBalance]             = useState(null);
  const [currency, setCurrency]           = useState("USD");
  const [accountType, setAccountType]     = useState(null);
  const [loginid, setLoginid]             = useState("");
  const [running, setRunning]             = useState(false);
  const [riskProfile, setRiskProfile]     = useState("balanced");
  const [baseStake, setBaseStake]         = useState(0.35);
  const [currentStake, setCurrentStake]   = useState(0.35);
  const [autoMarket, setAutoMarket]       = useState(true);
  const [manualMarket, setManualMarket]   = useState(null);
  const [bestMarket, setBestMarket]       = useState(null);
  const [manualContract, setManualContract] = useState("DIGITOVER");
  const [manualBarrier, setManualBarrier] = useState(4);
  const [tickData, setTickData]           = useState({});
  const [marketScores, setMarketScores]   = useState({});
  const [trades, setTrades]               = useState([]);
  const [activeContract, setActiveContract] = useState(null);
  const [consecLosses, setConsecLosses]   = useState(0);
  const [sessionProfit, setSessionProfit] = useState(0);
  const [stats, setStats]                 = useState({ wins: 0, losses: 0, profit: 0 });
  const [logs, setLogs]                   = useState([]);
  const [tab, setTab]                     = useState("dashboard");
  const [status, setStatus]               = useState("idle");
  const [countdown, setCountdown]         = useState(0);
  const [skipCount, setSkipCount]         = useState(0);
  // NEW: track last market switch event for UI
  const [lastSwitch, setLastSwitch]       = useState(null);

  const wsRef             = useRef(null);
  const pingTimer         = useRef(null);
  const tradeTimer        = useRef(null);
  const cdTimer           = useRef(null);
  // NEW: continuous background rescan timer
  const rescanTimer       = useRef(null);
  const runningRef        = useRef(false);
  const consecRef         = useRef(0);
  const sessionRef        = useRef(0);
  const stakeRef          = useRef(0.35);
  const baseStakeRef      = useRef(0.35);
  const riskRef           = useRef("balanced");
  const tickRef           = useRef({});
  const bestMarketRef     = useRef(null);
  const manualMarketRef   = useRef(null);
  const autoMarketRef     = useRef(true);
  const manualContractRef = useRef("DIGITOVER");
  const manualBarrierRef  = useRef(4);
  const activeRef         = useRef(false);
  const tokenRef          = useRef("");
  const skipCountRef      = useRef(0);
  // NEW: score of currently locked-in market for drift detection
  const currentMarketScoreRef = useRef(0);

  runningRef.current        = running;
  stakeRef.current          = currentStake;
  baseStakeRef.current      = baseStake;
  riskRef.current           = riskProfile;
  tickRef.current           = tickData;
  bestMarketRef.current     = bestMarket;
  manualMarketRef.current   = manualMarket;
  autoMarketRef.current     = autoMarket;
  manualContractRef.current = manualContract;
  manualBarrierRef.current  = manualBarrier;

  const log = useCallback((msg, type = "info") => {
    const ts = new Date().toLocaleTimeString();
    setLogs(p => [{ ts, msg, type }, ...p].slice(0, 200));
  }, []);

  const send = useCallback((obj) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(obj));
  }, []);

   // ── CORE: score all markets and return best ────────────────────────────────
   const scoreAllMarkets = useCallback(() => {
     const ticks = tickRef.current;
     const scores = {};
     let best = null, bestScore = -1;
     MARKETS.forEach(m => {
       const prices = ticks[m.symbol];
       if (prices && prices.length >= 30) {
         const s = scoreMarket(prices);
         scores[m.symbol] = s;
         if (s > bestScore) { bestScore = s; best = m; }
       }
     });
     setMarketScores(scores);
     return { best, bestScore, scores };
   }, []);

   // ── Background rescan: always switch to the hottest market immediately ─────
   // No threshold required — whenever volatility regime changes, jump to the
   // market with the highest score to lock in profits and avoid drawdowns.
   const runBackgroundRescan = useCallback(() => {
     const { best, bestScore, scores } = scoreAllMarkets();
     if (!best) return;

     const currentSym = bestMarketRef.current?.symbol;
     currentMarketScoreRef.current = currentSym ? (scores[currentSym] ?? 0) : 0;

     // Drift guard — current market no longer agrees or confidence collapsed
     if (currentSym && autoMarketRef.current) {
       const ticks = tickRef.current;
       const a = ticks[currentSym] ? analyseDigits(ticks[currentSym]) : null;
       if (a && (!a.agree || a.confidence < DRIFT_CONFIDENCE_FLOOR)) {
         log(`⚠️ ${bestMarketRef.current?.label} signal drifted (conf ${(a.confidence*100).toFixed(0)}%) — switching to ${best.label}`, "warn");
         setBestMarket(best);
         bestMarketRef.current = best;
         currentMarketScoreRef.current = bestScore;
         setLastSwitch({ from: bestMarketRef.current?.label, to: best.label, reason: "drift", time: new Date().toLocaleTimeString() });
         return;
       }
     }

     // Always switch to the highest-scoring market — no threshold needed.
     // This prevents the bot from bleeding profits by sticking to a cooling market.
     if (autoMarketRef.current && best.symbol !== currentSym) {
       const fromLabel = bestMarketRef.current?.label ?? "none";
       log(`🔀 Market switch: ${fromLabel} → ${best.label} (score ${currentMarketScoreRef.current.toFixed(0)} → ${bestScore.toFixed(0)})`, "warn");
       setBestMarket(best);
       bestMarketRef.current = best;
       currentMarketScoreRef.current = bestScore;
       setLastSwitch({ from: fromLabel, to: best.label, reason: "better", time: new Date().toLocaleTimeString() });
     }
   }, [scoreAllMarkets, log]);

   // ── pickBestMarket: always return the freshest best market ──────────────────
   const pickBestMarket = useCallback(() => {
    const { best, bestScore, scores } = scoreAllMarkets();
    if (best) {
      setBestMarket(best);
      bestMarketRef.current = best;
      currentMarketScoreRef.current = bestScore;
    }
    return best;
  }, [scoreAllMarkets]);

  // ── Build trade parameters ─────────────────────────────────────────────────
  const getTradeParams = useCallback(() => {
    const ticks   = tickRef.current;
    const profile = RISK[riskRef.current];

    // Always do a fresh pick right before trading
    // Always pick the freshest best market in auto mode
    const market = autoMarketRef.current
      ? pickBestMarket()
      : (manualMarketRef.current || null);

    if (!market) {
      return { market: null, contractType: "DIGITOVER", barrier: 4, skip: true, reason: "No market selected or waiting for data" };
    }

    const prices   = ticks[market.symbol];
    const analysis = prices ? analyseDigits(prices) : null;

    if (!analysis) {
      return { market, contractType: "DIGITOVER", barrier: 4, skip: true, reason: "Waiting for data (need 30+ ticks)" };
    }
    if (!analysis.agree) {
      // Market signal broke — rescan immediately for a better market
      if (autoMarketRef.current) {
        const fresh = pickBestMarket();
        if (fresh && fresh.symbol !== market.symbol) {
          log(`🔀 Signal lost on ${market.label} — switching to ${fresh.label}`, "warn");
          return { market: fresh, contractType: analysis.contractType, barrier: analysis.barrier, skip: true, reason: "Switched to better market" };
        }
      }
      return { market, contractType: analysis.contractType, barrier: analysis.barrier, skip: true, reason: "Windows disagree — signal unclear" };
    }
    if (!analysis.momentum || analysis.momentum < 0.6) {
      return {
        market, contractType: analysis.contractType, barrier: analysis.barrier,
        skip: true,
        reason: "Momentum " + Math.round((analysis.momentum || 0) * 100) + "% < 60%",
      };
    }
    if (analysis.confidence < profile.minConfidence) {
      return {
        market, contractType: analysis.contractType, barrier: analysis.barrier,
        skip: true,
        reason: `Confidence ${(analysis.confidence*100).toFixed(1)}% < ${(profile.minConfidence*100).toFixed(0)}% threshold`,
      };
    }

    return {
      market,
      contractType: autoMarketRef.current ? analysis.contractType : manualContractRef.current,
      barrier:      autoMarketRef.current ? analysis.barrier      : manualBarrierRef.current,
      skip: false, analysis,
    };
  }, [pickBestMarket, log]);

  const placeTrade = useCallback(() => {
    if (!runningRef.current || activeRef.current) return;

    const params = getTradeParams();
    if (params.skip) {
      skipCountRef.current++;
      setSkipCount(skipCountRef.current);
      log(`⏭ Skipped — ${params.reason}`, "warn");
      clearTimeout(tradeTimer.current);
      tradeTimer.current = setTimeout(() => { if (runningRef.current) placeTrade(); }, 2000);
      return;
    }

    const { market, contractType, barrier, analysis } = params;
    if (!market) return; // safety guard
    const stake = stakeRef.current;
    log(`🔄 ${contractType} @${barrier} | ${market.label} | $${stake} | Conf: ${(analysis.confidence*100).toFixed(1)}%`, "trade");
    setStatus("trading");
    activeRef.current = true;

    send({
      proposal: 1,
      amount:   stake,
      basis:    "stake",
      contract_type: contractType,
      currency: currency || "USD",
      duration: 1,
      duration_unit: "t",
      symbol:   market.symbol,
      barrier:  String(barrier),
    });
  }, [getTradeParams, log, send, currency]);

  const scheduleNext = useCallback((delayMs = 2500) => {
    clearTimeout(tradeTimer.current);
    clearInterval(cdTimer.current);
    let secs = Math.ceil(delayMs / 1000);
    setCountdown(secs);
    cdTimer.current = setInterval(() => {
      secs--;
      setCountdown(Math.max(secs, 0));
      if (secs <= 0) clearInterval(cdTimer.current);
    }, 1000);
    tradeTimer.current = setTimeout(() => { if (runningRef.current) placeTrade(); }, delayMs);
  }, [placeTrade]);

  const handleMessage = useCallback((data) => {
    if (data.msg_type === "ping" || data.pong) return;

    if (data.msg_type === "authorize") {
      if (data.error) { log("❌ Auth failed: " + data.error.message, "error"); return; }
      const auth = data.authorize;
      const isVirtual = auth.is_virtual === 1 || auth.is_virtual === true;
      const aType = isVirtual ? "virtual" : "real";
      setAccountType(aType);
      setLoginid(auth.loginid);
      setAuthorized(true);
      setBalance(auth.balance);
      setCurrency(auth.currency || "USD");
      log(`✅ Authorized: ${auth.loginid} | ${auth.balance} ${auth.currency} | ${aType.toUpperCase()}`, "success");
      if (isVirtual) log("ℹ️ Demo account — Digits on R_ markets fully supported", "info");
      MARKETS.forEach(m => send({ ticks_history: m.symbol, count: 100, end: "latest", style: "ticks", subscribe: 1 }));
      send({ balance: 1, subscribe: 1 });
    }

    if (data.msg_type === "balance") setBalance(data.balance.balance);

    if (data.msg_type === "history") {
      const sym    = data.echo_req.ticks_history;
      const prices = data.history.prices.map(Number);
      setTickData(prev => { const u = { ...prev, [sym]: prices }; tickRef.current = u; return u; });
    }

    if (data.msg_type === "tick") {
      const sym   = data.tick.symbol;
      const price = parseFloat(data.tick.quote);
      setTickData(prev => {
        const u = { ...prev, [sym]: [...(prev[sym] || []), price].slice(-150) };
        tickRef.current = u;
        return u;
      });
    }

    if (data.msg_type === "proposal") {
      if (data.error) {
        log("❌ Proposal error: " + data.error.message, "error");
        activeRef.current = false;
        if (runningRef.current) scheduleNext(4000);
        return;
      }
      send({ buy: data.proposal.id, price: data.proposal.ask_price });
    }

    if (data.msg_type === "buy") {
      if (data.error) {
        log("❌ Buy error: " + data.error.message, "error");
        const msg = (data.error.message || "").toLowerCase();
        if (msg.includes("account type") || msg.includes("not allowed") || msg.includes("permission")) {
          log("⛔ Account permission error — bot stopped.", "error");
          setRunning(false); runningRef.current = false; setStatus("idle");
          activeRef.current = false;
          return;
        }
        activeRef.current = false;
        if (runningRef.current) scheduleNext(4000);
        return;
      }
      const c = data.buy;
      setActiveContract({ id: c.contract_id, buyPrice: c.buy_price });
      log(`📝 #${c.contract_id} opened | Paid: $${c.buy_price}`, "trade");
      send({ proposal_open_contract: 1, contract_id: c.contract_id, subscribe: 1 });
    }

    if (data.msg_type === "proposal_open_contract") {
      const c = data.proposal_open_contract;
      if (!c.is_sold && c.status !== "won" && c.status !== "lost") return;

      const profit = parseFloat(c.profit || 0);
      const won    = c.status === "won";
      activeRef.current = false;
      setActiveContract(null);
      if (c.balance_after !== undefined) setBalance(c.balance_after);

      setTrades(prev => [{
        id: c.contract_id, time: new Date().toLocaleTimeString(),
        symbol: c.underlying, type: c.contract_type,
        stake: parseFloat(c.buy_price || 0).toFixed(2),
        profit, won, barrier: c.barrier,
      }, ...prev].slice(0, 100));

      setStats(prev => ({
        wins:   prev.wins   + (won ? 1 : 0),
        losses: prev.losses + (won ? 0 : 1),
        profit: parseFloat((prev.profit + profit).toFixed(2)),
      }));

      sessionRef.current = parseFloat((sessionRef.current + profit).toFixed(2));
      setSessionProfit(sessionRef.current);

      const profile = RISK[riskRef.current];

      if (won) {
        consecRef.current = 0;
        setConsecLosses(0);
        setCurrentStake(baseStakeRef.current);
        stakeRef.current = baseStakeRef.current;
        log(`✅ WIN +$${profit.toFixed(2)} | Session: ${sessionRef.current >= 0 ? "+" : ""}$${sessionRef.current}`, "success");
      } else {
        consecRef.current++;
        setConsecLosses(consecRef.current);
        const ns = parseFloat((stakeRef.current * profile.stakeMultiplier).toFixed(2));
        log(`❌ LOSS -$${Math.abs(profit).toFixed(2)} | Consec: ${consecRef.current} | Next stake: $${ns}`, "error");
        if (consecRef.current >= profile.maxConsecLoss) {
          log(`⛔ ${profile.maxConsecLoss} consecutive losses — paused`, "warn");
          setRunning(false); runningRef.current = false; setStatus("paused");
          return;
        }
        setCurrentStake(ns); stakeRef.current = ns;
      }

      if (sessionRef.current >= profile.takeProfit) {
        log(`🎯 Take profit +$${sessionRef.current} hit — session complete!`, "success");
        setRunning(false); runningRef.current = false; setStatus("idle"); return;
      }
      if (sessionRef.current <= -profile.stopLoss) {
        log(`🛑 Stop loss -$${Math.abs(sessionRef.current)} hit — stopped`, "error");
        setRunning(false); runningRef.current = false; setStatus("idle"); return;
      }

      // NEW: after each trade result, do a quick rescan before scheduling next
      if (runningRef.current && autoMarketRef.current) {
        runBackgroundRescan();
      }

      if (runningRef.current) scheduleNext(2500);
    }
  }, [log, send, scheduleNext, runBackgroundRescan]);

  const connect = useCallback((token) => {
    if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} }
    tokenRef.current = token;
    log("🔌 Connecting...", "info");
    const socket = new WebSocket(DERIV_WS_URL);
    wsRef.current = socket;
    socket.onopen = () => {
      setConnected(true);
      log("🟢 Connected", "success");
      pingTimer.current = setInterval(() => {
        if (socket.readyState === 1) socket.send(JSON.stringify({ ping: 1 }));
      }, 20000);
      socket.send(JSON.stringify({ authorize: token }));
    };
    socket.onmessage = (e) => handleMessage(JSON.parse(e.data));
    socket.onerror   = () => log("⚠️ Connection error", "error");
    socket.onclose   = () => {
      setConnected(false); setAuthorized(false);
      clearInterval(pingTimer.current);
      clearInterval(rescanTimer.current);
      log("🔴 Disconnected", "error");
      if (runningRef.current) { log("↻ Reconnecting in 4s...", "warn"); setTimeout(() => connect(tokenRef.current), 4000); }
    };
  }, [handleMessage, log]);

  // ── NEW: start/stop the background rescan loop alongside the bot ──────────
  const startRescanLoop = useCallback(() => {
    clearInterval(rescanTimer.current);
    rescanTimer.current = setInterval(() => {
      if (autoMarketRef.current) runBackgroundRescan();
    }, RESCAN_INTERVAL_MS);
  }, [runBackgroundRescan]);

  const stopRescanLoop = useCallback(() => {
    clearInterval(rescanTimer.current);
  }, []);

  const startBot = useCallback(() => {
    if (!authorized) { log("Not authorized", "error"); return; }
    consecRef.current = 0; sessionRef.current = 0; activeRef.current = false;
    skipCountRef.current = 0;
    setConsecLosses(0); setSessionProfit(0); setSkipCount(0); setLastSwitch(null);
    setCurrentStake(baseStake); stakeRef.current = baseStake;
    setStatus("running"); setRunning(true); runningRef.current = true;
    log("🚀 Bot started — smart signal filter + live market rescanning active", "success");
    startRescanLoop();
    setTimeout(() => { if (runningRef.current) placeTrade(); }, 1500);
  }, [authorized, baseStake, log, placeTrade, startRescanLoop]);

  const stopBot = useCallback(() => {
    setRunning(false); runningRef.current = false;
    clearTimeout(tradeTimer.current); clearInterval(cdTimer.current);
    stopRescanLoop();
    setCountdown(0); setStatus("idle");
    log("⏹ Stopped by user", "warn");
  }, [log, stopRescanLoop]);

  useEffect(() => () => {
    wsRef.current?.close();
    clearInterval(pingTimer.current);
    clearTimeout(tradeTimer.current);
    clearInterval(cdTimer.current);
    clearInterval(rescanTimer.current);
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const totalTrades = stats.wins + stats.losses;
  const winRate     = totalTrades > 0 ? ((stats.wins / totalTrades) * 100).toFixed(1) : "0.0";
  const profile     = RISK[riskProfile];
  const isVirtual   = accountType === "virtual";
  const topMarkets  = Object.entries(marketScores).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const activeMarket = autoMarket ? bestMarket : manualMarket;
  const liveA = (() => {
    const m = activeMarket; if (!m) return null;
    const p = tickData[m.symbol]; return p ? analyseDigits(p) : null;
  })();

  return (
    <div style={{ minHeight:"100vh", background:"#060b11", fontFamily:"'JetBrains Mono','Courier New',monospace", color:"#dce8f2" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Syne:wght@800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:#0d1520}::-webkit-scrollbar-thumb{background:#1e3a5f;border-radius:2px}
        .inp{background:#0c1928;border:1px solid #1e3a5f;color:#dce8f2;padding:8px 12px;border-radius:6px;font-family:inherit;font-size:13px;outline:none;width:100%}
        .inp:focus{border-color:#00d4aa;box-shadow:0 0 0 2px rgba(0,212,170,.12)}
        .sel{background:#0c1928;border:1px solid #1e3a5f;color:#dce8f2;padding:8px 12px;border-radius:6px;font-family:inherit;font-size:13px;outline:none;cursor:pointer;width:100%}
        .card{background:#0c1928;border:1px solid #162840;border-radius:12px;padding:20px}
        .tab{background:none;border:none;cursor:pointer;padding:8px 16px;font-family:inherit;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;transition:all .2s}
        .tab.on{color:#00d4aa;border-bottom:2px solid #00d4aa}
        .tab.off{color:#2e4a6a;border-bottom:2px solid transparent}
        .tab.off:hover{color:#6a8ab2}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
        @keyframes slidein{from{opacity:0;transform:translateY(-5px)}to{opacity:1;transform:translateY(0)}}
        @keyframes glow{0%,100%{box-shadow:0 0 10px rgba(0,212,170,.25)}50%{box-shadow:0 0 22px rgba(0,212,170,.55)}}
        .pulse{animation:pulse 1.4s infinite}
        .slidein{animation:slidein .2s ease}
        .glow{animation:glow 2s infinite}
      `}</style>

      {/* HEADER */}
      <div style={{ background:"#08111c", borderBottom:"1px solid #162840", padding:"0 24px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", height:56 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:33, height:33, background:"linear-gradient(135deg,#00d4aa,#0096c7)", borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>⚡</div>
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:16, fontWeight:800, lineHeight:1.1 }}>
                DERIV<span style={{ color:"#00d4aa" }}>BOT</span>
                <span style={{ fontSize:9, color:"#2e4a6a", fontFamily:"monospace", marginLeft:6 }}>PRO v4</span>
              </div>
              <div style={{ fontSize:8, color:"#2e4a6a", letterSpacing:2 }}>ADAPTIVE MARKET ENGINE</div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {authorized && balance !== null && (
              <div style={{ background:"#0c1928", border:"1px solid #162840", borderRadius:8, padding:"5px 14px", fontSize:13 }}>
                <span style={{ color:"#2e4a6a" }}>BAL </span>
                <span style={{ color:"#00d4aa", fontWeight:700 }}>{parseFloat(balance).toFixed(2)} {currency}</span>
                <span style={{ marginLeft:8, fontSize:9, padding:"2px 7px", borderRadius:20, fontWeight:700,
                  background: isVirtual?"#2a1500":"#001a0e",
                  color: isVirtual?"#f4a261":"#00d4aa",
                  border:`1px solid ${isVirtual?"#f4a261":"#00d4aa"}`
                }}>{isVirtual?"DEMO":"REAL"}</span>
              </div>
            )}
            {running && (
              <div className="pulse" style={{ background:"#001a0e", border:"1px solid #00d4aa", borderRadius:8, padding:"5px 13px", fontSize:11, color:"#00d4aa", fontWeight:700 }}>
                ● LIVE{countdown > 0 ? ` · ${countdown}s` : ""}
              </div>
            )}
            <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:10 }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background: authorized?"#00d4aa":connected?"#f4a261":"#e63946" }} className={connected?"pulse":""} />
              <span style={{ color: authorized?"#00d4aa":connected?"#f4a261":"#e63946", fontWeight:700 }}>
                {authorized?"AUTHORIZED":connected?"CONNECTED":"OFFLINE"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div style={{ background:"#08111c", borderBottom:"1px solid #162840", padding:"0 24px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto", display:"flex" }}>
          {[["dashboard","📊 Dashboard"],["markets","📈 Markets"],["trades","💼 Trades"],["settings","⚙️ Settings"],["logs","📋 Logs"]].map(([t,l])=>(
            <button key={t} className={`tab ${tab===t?"on":"off"}`} onClick={()=>setTab(t)}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:1200, margin:"0 auto", padding:24 }}>

        {/* ══ DASHBOARD ══ */}
        {tab==="dashboard" && (
          <div>
            {/* Connect */}
            {!authorized && (
              <div className="card" style={{ marginBottom:18 }}>
                <div style={{ color:"#00d4aa", fontSize:10, fontWeight:700, letterSpacing:2, marginBottom:12 }}>CONNECT TO DERIV</div>
                <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                  <input className="inp" style={{ flex:1, minWidth:220 }} type="password"
                    placeholder="Paste API Token (Read + Trade)..."
                    value={apiToken} onChange={e=>setApiToken(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&connect(apiToken)} />
                  <button onClick={()=>connect(apiToken)} disabled={!apiToken.trim()} style={{
                    background:apiToken.trim()?"linear-gradient(135deg,#00d4aa,#0096c7)":"#1e3a5f",
                    border:"none",color:"#fff",padding:"8px 24px",borderRadius:8,
                    cursor:apiToken.trim()?"pointer":"not-allowed",
                    fontFamily:"inherit",fontWeight:700,fontSize:13,letterSpacing:1
                  }}>CONNECT</button>
                </div>
                <div style={{ marginTop:8, fontSize:11, color:"#2e4a6a" }}>
                  Get token: <span style={{ color:"#00b4d8" }}>app.deriv.com → Settings → API Token</span> — enable <b>Read</b> + <b>Trade</b>
                </div>
              </div>
            )}

            {/* Stats */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:16 }}>
              {[
                { l:"SESSION P/L",  v:`${sessionProfit>=0?"+":""}$${sessionProfit.toFixed(2)}`, c:sessionProfit>0?"#00d4aa":sessionProfit<0?"#e63946":"#5a7a9a" },
                { l:"WIN RATE",     v:`${winRate}%`, c:parseFloat(winRate)>=55?"#00d4aa":parseFloat(winRate)>=45?"#f4a261":"#e63946" },
                { l:"TRADES",       v:totalTrades,   c:"#dce8f2" },
                { l:"WINS",         v:stats.wins,    c:"#00d4aa" },
                { l:"LOSSES",       v:stats.losses,  c:"#e63946" },
                { l:"SKIPPED 🧠",  v:skipCount,     c:"#f4a261" },
                { l:"ALL-TIME P/L", v:`${stats.profit>=0?"+":""}$${stats.profit.toFixed(2)}`, c:stats.profit>=0?"#00d4aa":"#e63946" },
              ].map(s=>(
                <div key={s.l} className="card" style={{ padding:"11px 14px" }}>
                  <div style={{ fontSize:8, color:"#2e4a6a", fontWeight:700, letterSpacing:1.5, marginBottom:5 }}>{s.l}</div>
                  <div style={{ fontSize:19, fontWeight:700, color:s.c }}>{s.v}</div>
                </div>
              ))}
            </div>

            {/* NEW: last market switch banner */}
            {lastSwitch && (
              <div className="slidein" style={{ background:"#0e1f10", border:"1px solid #f4a261", borderRadius:8, padding:"9px 14px", marginBottom:12, fontSize:11, display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ color:"#f4a261", fontWeight:700 }}>🔀 MARKET SWITCHED</span>
                <span style={{ color:"#4a6a8a" }}>{lastSwitch.from} → </span>
                <span style={{ color:"#00d4aa", fontWeight:700 }}>{lastSwitch.to}</span>
                <span style={{ color:"#2e4a6a", marginLeft:"auto" }}>{lastSwitch.time}</span>
                <span style={{ fontSize:9, padding:"2px 8px", borderRadius:12, background:"#1e2c10", color:"#a0c060" }}>
                  {lastSwitch.reason === "drift" ? "signal drifted" : "better edge found"}
                </span>
              </div>
            )}

            {/* Live signal panel */}
            {liveA && authorized && (
              <div className="card" style={{ marginBottom:14, padding:"12px 16px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                  <div>
                    <div style={{ fontSize:8, color:"#2e4a6a", letterSpacing:1.5, marginBottom:3 }}>SIGNAL</div>
                    <div style={{ fontSize:13, fontWeight:700,
                      color: !liveA.agree?"#e63946":liveA.confidence>=0.68?"#00d4aa":liveA.confidence>=0.60?"#f4a261":"#e63946"
                    }}>
                      {!liveA.agree ? "🔴 MIXED — Skip" : liveA.confidence>=0.68 ? "🟢 STRONG" : liveA.confidence>=0.60 ? "🟡 MODERATE" : "🔴 WEAK"}
                    </div>
                  </div>
                  <div style={{ width:1, height:32, background:"#162840" }} />
                  <div>
                    <div style={{ fontSize:8, color:"#2e4a6a", letterSpacing:1.5, marginBottom:3 }}>STRATEGY</div>
                    <div style={{ fontSize:13, fontWeight:700, color:liveA.contractType==="DIGITOVER"?"#00b4d8":"#f4a261" }}>
                      {liveA.contractType} @ {liveA.barrier}
                    </div>
                  </div>
                  <div style={{ width:1, height:32, background:"#162840" }} />
                  <div>
                    <div style={{ fontSize:8, color:"#2e4a6a", letterSpacing:1.5, marginBottom:3 }}>CONFIDENCE</div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#dce8f2" }}>{(liveA.confidence*100).toFixed(1)}%</div>
                  </div>
                  <div style={{ width:1, height:32, background:"#162840" }} />
                  <div>
                    <div style={{ fontSize:8, color:"#2e4a6a", letterSpacing:1.5, marginBottom:3 }}>MARKET</div>
                    <div style={{ fontSize:12, fontWeight:700 }}>{activeMarket?.label||"—"}</div>
                  </div>
                  <div style={{ marginLeft:"auto", minWidth:180 }}>
                    <div style={{ fontSize:8, color:"#2e4a6a", marginBottom:4 }}>
                      OVER {(liveA.overRate*100).toFixed(0)}% — UNDER {(liveA.underRate*100).toFixed(0)}%
                    </div>
                    <div style={{ display:"flex", height:5, borderRadius:3, overflow:"hidden" }}>
                      <div style={{ width:`${liveA.overRate*100}%`, background:"#00b4d8" }} />
                      <div style={{ flex:1, background:"#f4a261" }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
              {/* Config */}
              <div className="card">
                <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2, marginBottom:14 }}>TRADE CONFIG</div>
                <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
                  <div>
                    <div style={{ fontSize:10, color:"#2e4a6a", marginBottom:4 }}>BASE STAKE ({currency})</div>
                    <input className="inp" type="number" min="0.35" step="0.25" value={baseStake}
                      onChange={e=>{const v=parseFloat(e.target.value);setBaseStake(v);if(!running)setCurrentStake(v);}} />
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:"#2e4a6a", marginBottom:4 }}>RISK PROFILE</div>
                    <select className="sel" value={riskProfile} onChange={e=>setRiskProfile(e.target.value)}>
                      <option value="conservative">🛡 Conservative  TP+${RISK.conservative.takeProfit} SL-${RISK.conservative.stopLoss}</option>
                      <option value="balanced">⚖️ Balanced  TP+${RISK.balanced.takeProfit} SL-${RISK.balanced.stopLoss}</option>
                      <option value="aggressive">🔥 Aggressive  TP+${RISK.aggressive.takeProfit} SL-${RISK.aggressive.stopLoss}</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:"#2e4a6a", marginBottom:6 }}>MARKET MODE</div>
                    <div style={{ display:"flex", gap:8 }}>
                      {[true,false].map(v=>(
                        <button key={String(v)} onClick={()=>setAutoMarket(v)} style={{
                          flex:1, padding:"7px", borderRadius:8,
                          border:`1px solid ${autoMarket===v?"#00d4aa":"#1e3a5f"}`,
                          background:autoMarket===v?"#001a0e":"transparent",
                          color:autoMarket===v?"#00d4aa":"#2e4a6a",
                          cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700
                        }}>{v?"🤖 AUTO":"✋ MANUAL"}</button>
                      ))}
                    </div>
                    {!autoMarket && (
                      <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:8 }}>
                        <select className="sel" value={manualMarket?.symbol||""} onChange={e=>setManualMarket(MARKETS.find(m=>m.symbol===e.target.value)||null)}>
                          <option value="">— Choose market —</option>
                          {MARKETS.map(m=><option key={m.symbol} value={m.symbol}>{m.label}</option>)}
                        </select>
                        <select className="sel" value={manualContract} onChange={e=>setManualContract(e.target.value)}>
                          <option value="DIGITOVER">OVER (last digit &gt; barrier)</option>
                          <option value="DIGITUNDER">UNDER (last digit &lt; barrier)</option>
                        </select>
                        <div>
                          <div style={{ fontSize:10, color:"#2e4a6a", marginBottom:3 }}>BARRIER: {manualBarrier}</div>
                          <input className="inp" type="range" min="0" max="9" value={manualBarrier}
                            onChange={e=>setManualBarrier(parseInt(e.target.value))} style={{ padding:0, cursor:"pointer" }} />
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#2e4a6a", marginTop:2 }}>
                            {[0,1,2,3,4,5,6,7,8,9].map(i=>(
                              <span key={i} style={{ color:i===manualBarrier?"#00d4aa":"#2e4a6a" }}>{i}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="card">
                <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2, marginBottom:14 }}>LIVE STATUS</div>
                <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                  {[
                    { l:"Status",         v:status.toUpperCase(),                                c:running?"#00d4aa":status==="paused"?"#f4a261":"#2e4a6a" },
                    { l:"Account",        v:loginid?`${loginid} [${(accountType||"").toUpperCase()}]`:"—", c:isVirtual?"#f4a261":"#dce8f2" },
                    { l:"Market",         v:activeMarket?.label||"Scanning...",                  c:"#dce8f2" },
                    { l:"Current Stake",  v:`$${currentStake.toFixed(2)} ${currency}`,           c:"#dce8f2" },
                    { l:"Consec. Losses", v:consecLosses,                                        c:consecLosses>=profile.maxConsecLoss?"#e63946":consecLosses>0?"#f4a261":"#00d4aa" },
                    { l:"Min Confidence", v:`${(profile.minConfidence*100).toFixed(0)}%`,        c:"#5a8ab0" },
                    { l:"Take Profit",    v:`+$${profile.takeProfit}`,                           c:"#00d4aa" },
                    { l:"Stop Loss",      v:`-$${profile.stopLoss}`,                             c:"#e63946" },
                    // NEW: rescan indicator
                    { l:"Market Rescan",  v:running && autoMarket ? `every ${RESCAN_INTERVAL_MS/1000}s` : "off", c:running&&autoMarket?"#00b4d8":"#2e4a6a" },
                  ].map(r=>(
                    <div key={r.l} style={{ display:"flex", justifyContent:"space-between", fontSize:12, borderBottom:"1px solid #101e2e", paddingBottom:8 }}>
                      <span style={{ color:"#2e4a6a" }}>{r.l}</span>
                      <span style={{ color:r.c, fontWeight:600 }}>{r.v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop:12 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#2e4a6a", marginBottom:4 }}>
                    <span>SESSION</span><span style={{ color:"#00d4aa" }}>TARGET +${profile.takeProfit}</span>
                  </div>
                  <div style={{ height:5, background:"#070f18", borderRadius:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${Math.min(Math.max((sessionProfit/profile.takeProfit)*100,0),100)}%`, background:"linear-gradient(90deg,#00d4aa,#0096c7)", borderRadius:3, transition:"width .4s" }} />
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#2e4a6a", marginTop:2 }}>
                    <span style={{ color:sessionProfit>=0?"#00d4aa":"#e63946" }}>${sessionProfit.toFixed(2)}</span>
                    <span>${profile.takeProfit}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Active contract */}
            {activeContract && (
              <div className="glow" style={{ background:"#001525", border:"1px solid #00d4aa", borderRadius:10, padding:"11px 16px", marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:13 }}>
                <span>🔄 <span style={{ color:"#00d4aa", fontWeight:700 }}>CONTRACT LIVE</span> #{activeContract.id}</span>
                <span style={{ color:"#5a8ab0" }}>Stake: <strong style={{ color:"#dce8f2" }}>${activeContract.buyPrice}</strong></span>
                <span className="pulse" style={{ color:"#f4a261" }}>⏳ Awaiting result...</span>
              </div>
            )}

            {/* Start/Stop */}
            <button onClick={running?stopBot:startBot} disabled={!authorized} style={{
              width:"100%", padding:17, borderRadius:12, border:"none",
              cursor:authorized?"pointer":"not-allowed",
              background:!authorized?"#1e3a5f":running
                ?"linear-gradient(135deg,#e63946,#9b2226)"
                :"linear-gradient(135deg,#00d4aa,#0096c7)",
              color:"#fff",fontFamily:"inherit",fontSize:15,fontWeight:800,letterSpacing:3,
              boxShadow:authorized?(running?"0 0 24px rgba(230,57,70,.3)":"0 0 24px rgba(0,212,170,.3)"):"none",
              transition:"all .3s"
            }}>
              {!authorized?"🔌 CONNECT FIRST":running?"⏹  STOP BOT":"▶  START BOT"}
            </button>

            {status==="paused" && (
              <div style={{ marginTop:12, background:"#180e00", border:"1px solid #f4a261", borderRadius:10, padding:12, fontSize:12, color:"#f4a261", textAlign:"center" }}>
                ⚠️ Paused after {profile.maxConsecLoss} consecutive losses. Stake reset. Click START to begin a new session.
              </div>
            )}

            {/* How it works */}
            <div className="card" style={{ marginTop:14, padding:14 }}>
              <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2, marginBottom:10 }}>🧠 ADAPTIVE MARKET ENGINE — HOW IT PROTECTS YOU</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:11, color:"#4a6a8a" }}>
                {[
                  ["Continuous Rescan",`Background rescan every ${RESCAN_INTERVAL_MS/1000}s — detects volatility regime changes even between trades`],
                  ["Drift Guard",`If current market confidence drops below ${(DRIFT_CONFIDENCE_FLOOR*100).toFixed(0)}% the bot exits and finds a fresh edge immediately`],
                  ["Switch Threshold",`Only switches if new market scores ${MARKET_SWITCH_THRESHOLD}+ points higher — avoids thrashing on noise`],
                  ["Pre-Trade Re-check","Final market re-validation right before each contract is sent — never trades on stale market selection"],
                ].map(([t,d])=>(
                  <div key={t} style={{ background:"#070f18", borderRadius:8, padding:"8px 12px", border:"1px solid #101e2e" }}>
                    <div style={{ color:"#00d4aa", fontWeight:700, marginBottom:2, fontSize:11 }}>{t}</div>
                    <div style={{ fontSize:10 }}>{d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ MARKETS ══ */}
        {tab==="markets" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2 }}>LIVE MARKET SCANNER</div>
              {running && autoMarket && (
                <div style={{ fontSize:10, color:"#00b4d8" }} className="pulse">● RESCANNING EVERY {RESCAN_INTERVAL_MS/1000}s</div>
              )}
            </div>
            <div className="card">
              {topMarkets.length===0
                ? <div style={{ color:"#2e4a6a", textAlign:"center", padding:40 }}>Connect to load market data...</div>
                : topMarkets.map(([sym,score],i)=>{
                  const mkt=MARKETS.find(m=>m.symbol===sym);
                  const a=tickData[sym]?analyseDigits(tickData[sym]):null;
                  return (
                    <div key={sym} className="slidein" style={{
                      display:"flex", alignItems:"center", gap:12, marginBottom:10,
                      padding:"10px 14px", borderRadius:8,
                      background:bestMarket?.symbol===sym?"#001a0e":"#070f18",
                      border:`1px solid ${bestMarket?.symbol===sym?"#00d4aa":"#162840"}`,
                    }}>
                      <div style={{ width:25, height:25, background:i===0?"linear-gradient(135deg,#f4d03f,#e6ac00)":"#162840", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0, color:i===0?"#000":"#dce8f2" }}>{i+1}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:13, fontWeight:600, color:mkt?.color }}>{mkt?.label}</span>
                          {a && (
                            <span style={{ fontSize:10, color:"#4a6a8a" }}>
                              {a.contractType} @{a.barrier} · {(a.confidence*100).toFixed(0)}%
                              {!a.agree?" 🔴":a.confidence>=0.68?" 🟢":a.confidence>=0.60?" 🟡":" 🔴"}
                            </span>
                          )}
                        </div>
                        <div style={{ height:4, background:"#162840", borderRadius:2, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${Math.min(score,100)}%`, background:`linear-gradient(90deg,${mkt?.color},#0096c7)`, borderRadius:2, transition:"width .5s" }} />
                        </div>
                      </div>
                      <span style={{ fontSize:12, fontWeight:700, color:mkt?.color, minWidth:28, textAlign:"right" }}>{score.toFixed(0)}</span>
                      {bestMarket?.symbol===sym && <span style={{ fontSize:9, color:"#00d4aa", fontWeight:700 }}>✓</span>}
                    </div>
                  );
                })
              }
            </div>

            {bestMarket&&tickData[bestMarket.symbol]&&(()=>{
              const prices=tickData[bestMarket.symbol];
              const a=analyseDigits(prices);
              if(!a)return null;
              const maxF=Math.max(...a.freq,1);
              return (
                <div className="card" style={{ marginTop:14 }}>
                  <div style={{ fontSize:10, color:"#2e4a6a", marginBottom:12 }}>DIGIT DISTRIBUTION — {bestMarket.label} (last {a.total} ticks)</div>
                  <div style={{ display:"flex", gap:6, alignItems:"flex-end", height:90 }}>
                    {a.freq.map((c,i)=>{
                      const pct=(c/a.total)*100;
                      const h=Math.max((c/maxF)*72,3);
                      const isWin=(a.contractType==="DIGITOVER"&&i>a.barrier)||(a.contractType==="DIGITUNDER"&&i<a.barrier);
                      return (
                        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                          <div style={{ fontSize:8, color:"#2e4a6a" }}>{pct.toFixed(0)}%</div>
                          <div style={{ width:"100%", height:`${h}px`, background:isWin?"#00d4aa":i===a.barrier?"#f4a261":"#162840", borderRadius:2, transition:"height .4s" }} />
                          <div style={{ fontSize:10, color:isWin?"#00d4aa":i===a.barrier?"#f4a261":"#4a6a8a", fontWeight:isWin?700:400 }}>{i}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop:10, display:"flex", gap:6, fontSize:10, flexWrap:"wrap" }}>
                    <div style={{ background:"#070f18", padding:"4px 10px", borderRadius:6 }}>🔥 Hot: {a.hotDigits.slice(0,3).map(d=>d.d).join(", ")}</div>
                    <div style={{ background:"#001a0e", padding:"4px 10px", borderRadius:6, color:"#00d4aa" }}>📊 {a.contractType} @{a.barrier}</div>
                    <div style={{ background:"#070f18", padding:"4px 10px", borderRadius:6 }}>Confidence: {(a.confidence*100).toFixed(1)}%</div>
                    <div style={{ background:"#070f18", padding:"4px 10px", borderRadius:6, color:a.agree?"#00d4aa":"#e63946" }}>
                      Windows: {a.agree?"✅ All Agree":"⚠️ Mixed"}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ══ TRADES ══ */}
        {tab==="trades" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2 }}>TRADE HISTORY ({trades.length})</div>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <span style={{ fontSize:11, color:"#00d4aa" }}>W: {stats.wins}</span>
                <span style={{ fontSize:11, color:"#e63946" }}>L: {stats.losses}</span>
                <span style={{ fontSize:11, color:parseFloat(winRate)>=50?"#00d4aa":"#e63946" }}>WR: {winRate}%</span>
                <button onClick={()=>setTrades([])} style={{ background:"none",border:"1px solid #162840",color:"#2e4a6a",padding:"3px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontSize:10 }}>CLEAR</button>
              </div>
            </div>
            {trades.length===0
              ? <div className="card" style={{ padding:40, textAlign:"center", color:"#2e4a6a" }}>No trades yet.</div>
              : (
                <div className="card" style={{ padding:0, overflow:"hidden" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"72px 90px 115px 65px 65px 80px 80px", background:"#070f18", padding:"9px 14px", fontSize:9, color:"#2e4a6a", fontWeight:700, letterSpacing:1 }}>
                    <span>TIME</span><span>MARKET</span><span>TYPE</span><span>STAKE</span><span>BARRIER</span><span>P/L</span><span>RESULT</span>
                  </div>
                  {trades.map((t,i)=>(
                    <div key={`${t.id}-${i}`} className="slidein" style={{ display:"grid", gridTemplateColumns:"72px 90px 115px 65px 65px 80px 80px", padding:"9px 14px", borderTop:"1px solid #101e2e", fontSize:12, background:i%2===0?"#0c1928":"#070f18" }}>
                      <span style={{ color:"#2e4a6a", fontSize:11 }}>{t.time}</span>
                      <span style={{ color:"#4a6a8a", fontSize:10 }}>{t.symbol}</span>
                      <span style={{ color:t.type?.includes("OVER")?"#00b4d8":"#f4a261", fontSize:10 }}>{t.type}</span>
                      <span>${t.stake}</span>
                      <span style={{ color:"#4a6a8a" }}>{t.barrier}</span>
                      <span style={{ fontWeight:700, color:t.profit>=0?"#00d4aa":"#e63946" }}>{t.profit>=0?"+":""}{parseFloat(t.profit).toFixed(2)}</span>
                      <span style={{ color:t.won?"#00d4aa":"#e63946", fontWeight:700 }}>{t.won?"✅ WIN":"❌ LOSS"}</span>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}

        {/* ══ SETTINGS ══ */}
        {tab==="settings" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18 }}>
            <div className="card">
              <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2, marginBottom:14 }}>RISK PROFILES</div>
              {Object.entries(RISK).map(([key,p])=>(
                <div key={key} onClick={()=>setRiskProfile(key)} style={{
                  padding:13, border:`1px solid ${riskProfile===key?"#00d4aa":"#162840"}`,
                  borderRadius:8, marginBottom:8, cursor:"pointer",
                  background:riskProfile===key?"#001a0e":"transparent", transition:"all .2s"
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                    <span style={{ fontWeight:700, color:riskProfile===key?"#00d4aa":"#dce8f2", textTransform:"capitalize" }}>{key}</span>
                    {riskProfile===key && <span style={{ fontSize:9, color:"#00d4aa" }}>● ACTIVE</span>}
                  </div>
                  <div style={{ fontSize:10, color:"#2e4a6a", display:"flex", gap:12, flexWrap:"wrap" }}>
                    <span>TP: <b style={{ color:"#00d4aa" }}>+${p.takeProfit}</b></span>
                    <span>SL: <b style={{ color:"#e63946" }}>-${p.stopLoss}</b></span>
                    <span>Martingale: <b style={{ color:"#f4a261" }}>×{p.stakeMultiplier}</b></span>
                    <span>Max consec: <b style={{ color:"#dce8f2" }}>{p.maxConsecLoss}</b></span>
                    <span>Min conf: <b style={{ color:"#00b4d8" }}>{(p.minConfidence*100).toFixed(0)}%</b></span>
                  </div>
                </div>
              ))}
            </div>
            <div className="card">
              <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2, marginBottom:14 }}>ADAPTIVE ENGINE SETTINGS</div>
              <div style={{ fontSize:11, color:"#4a6a8a", lineHeight:1.9 }}>
                {[
                  ["Rescan Interval",`Every ${RESCAN_INTERVAL_MS/1000}s the bot re-scores all 10 markets in the background.`],
                  ["Switch Threshold",`A market swap only fires when the new leader is ${MARKET_SWITCH_THRESHOLD}+ score points ahead — prevents noise-driven thrashing.`],
                  ["Drift Floor",`If the active market's confidence falls below ${(DRIFT_CONFIDENCE_FLOOR*100).toFixed(0)}% the engine immediately moves to the best available market.`],
                  ["Pre-Trade Validation","Right before each contract is placed, the full market selection is re-run on current tick data — never relies on stale state."],
                  ["Post-Trade Rescan","After every trade result (win or loss) a fresh rescan runs before the next trade delay countdown."],
                  ["Manual Mode","In manual mode the adaptive engine is paused — you control market and contract type entirely."],
                ].map(([t,d])=>(
                  <div key={t} style={{ paddingBottom:9, marginBottom:9, borderBottom:"1px solid #101e2e" }}>
                    <span style={{ color:"#00d4aa", fontWeight:700 }}>{t}: </span>{d}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ LOGS ══ */}
        {tab==="logs" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <div style={{ fontSize:10, color:"#00d4aa", fontWeight:700, letterSpacing:2 }}>ACTIVITY LOG ({logs.length})</div>
              <button onClick={()=>setLogs([])} style={{ background:"none",border:"1px solid #162840",color:"#2e4a6a",padding:"3px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontSize:10 }}>CLEAR</button>
            </div>
            <div style={{ background:"#070f18", border:"1px solid #162840", borderRadius:12, padding:14, maxHeight:520, overflowY:"auto" }}>
              {logs.length===0
                ? <div style={{ color:"#2e4a6a", fontSize:12 }}>No activity yet.</div>
                : logs.map((l,i)=>(
                  <div key={i} style={{ fontSize:11, marginBottom:5, display:"flex", gap:12, borderBottom:"1px solid #0c1520", paddingBottom:4 }}>
                    <span style={{ color:"#2e4a6a", flexShrink:0, minWidth:74 }}>{l.ts}</span>
                    <span style={{ color:l.type==="success"?"#00d4aa":l.type==="error"?"#e63946":l.type==="warn"?"#f4a261":l.type==="trade"?"#00b4d8":"#4a6a8a" }}>{l.msg}</span>
                  </div>
                ))
              }
            </div>
          </div>
        )}

      </div>
    </div>
  );
}